// Consumer-side SSE client for POST /me/coach/messages/stream.
//
// Uses `fetch()` with manual stream reading (not EventSource) because we
// need to POST a JSON body AND send an Authorization header. EventSource
// supports neither — it's GET-only with no custom headers. Reading
// `response.body` as a stream lets us keep the Bearer-token auth model the
// rest of the SPA already uses while still consuming SSE frames.
//
// Event shape: see shared/coach-api.ts → CoachStreamEvent (discriminated
// union of 'stage' | 'done' | 'error'). We parse one frame at a time and
// hand it to the caller via `onEvent`. The caller renders stage copy on
// each 'stage', hydrates the thread on 'done', and shows an error state on
// 'error'.
//
// Each SSE frame is terminated by a blank line (`\n\n`). We buffer partial
// chunks across reads because `TextDecoder` + `ReadableStream` can hand us
// mid-frame bytes. Lines starting with `data:` carry the JSON payload; the
// `event:` line is informational — we dispatch on `evt.kind` from the
// payload, which carries the discriminant.

import type { CoachStreamEvent, SendCoachMessageRequest } from '@gc-coach/shared';
import { env } from '../config/env.js';
import { loadTokens } from '../auth/tokenStorage.js';

export type CoachStreamResult =
  | { ok: true }
  | { ok: false; code: string; message: string };

export interface SendCoachMessageStreamOptions {
  body: SendCoachMessageRequest;
  onEvent: (event: CoachStreamEvent) => void;
  signal?: AbortSignal;
}

/** True when a streaming endpoint is configured for this build. */
export function isCoachStreamEnabled(): boolean {
  return typeof env.coachStreamUrl === 'string' && env.coachStreamUrl.length > 0;
}

export async function sendCoachMessageStream(
  opts: SendCoachMessageStreamOptions,
): Promise<CoachStreamResult> {
  if (!env.coachStreamUrl) {
    return { ok: false, code: 'STREAM_DISABLED', message: 'Streaming not configured' };
  }
  const tokens = loadTokens();
  if (!tokens) {
    return { ok: false, code: 'UNAUTHORIZED', message: 'No active session' };
  }

  const bodyText = JSON.stringify(opts.body);
  // CloudFront OAC's default SigV4 signing of our Function URL origin
  // doesn't produce an x-amz-content-sha256 value that Lambda will accept
  // for POST-with-body requests — the signature mismatch surfaces as
  // InvalidSignatureException at the Lambda frontend. Workaround that AWS
  // documents: compute the SHA-256 of the request body on the client,
  // send it as the x-amz-content-sha256 header. OAC then signs with that
  // value, Lambda recomputes the same hash from the received body, they
  // match, signature validates. Browsers have SubtleCrypto for exactly
  // this (synchronous enough — one await before the fetch, no library).
  const payloadHashHex = await sha256Hex(bodyText);

  let response: Response;
  try {
    const init: RequestInit = {
      method: 'POST',
      headers: {
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
        // Deliberately NOT the Authorization header: CloudFront OAC uses that
        // header for its own SigV4 signature on the way to the Function URL
        // and overwrites whatever the client sent. X-Id-Token sidesteps the
        // conflict — OAC leaves it alone, the handler reads it as-is. The
        // non-streaming /me/coach/messages path (API Gateway, no OAC) still
        // uses Authorization: Bearer per the shared apiFetch convention.
        'X-Id-Token': tokens.idToken,
        'x-amz-content-sha256': payloadHashHex,
      },
      body: bodyText,
    };
    if (opts.signal) init.signal = opts.signal;
    response = await fetch(env.coachStreamUrl, init);
  } catch (err) {
    return { ok: false, code: 'NETWORK', message: (err as Error).message || 'Network error' };
  }

  if (!response.ok || !response.body) {
    // Non-200: Lambda rejected before any streaming happened (missing auth,
    // bad JSON, etc.). The body is usually a plain JSON error object; try to
    // parse it to preserve the server's `code`/`message`.
    try {
      const parsed = (await response.json()) as { code?: string; message?: string };
      return {
        ok: false,
        code: parsed.code ?? 'HTTP_ERROR',
        message: parsed.message ?? `HTTP ${response.status}`,
      };
    } catch {
      return { ok: false, code: 'HTTP_ERROR', message: `HTTP ${response.status}` };
    }
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let sawTerminalEvent = false;

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // Parse out whatever complete frames the buffer currently holds. SSE
      // delimits events with a blank line (`\n\n`); handle `\r\n\r\n` too so
      // intermediate proxies that rewrite line endings don't trip us.
      let sepIdx = findFrameSeparator(buffer);
      while (sepIdx !== -1) {
        const frameText = buffer.slice(0, sepIdx.start);
        buffer = buffer.slice(sepIdx.end);
        const event = parseFrame(frameText);
        if (event) {
          opts.onEvent(event);
          if (event.kind === 'done' || event.kind === 'error') sawTerminalEvent = true;
        }
        sepIdx = findFrameSeparator(buffer);
      }
    }
  } catch (err) {
    return {
      ok: false,
      code: 'STREAM_READ',
      message: (err as Error).message || 'Stream read error',
    };
  }

  if (!sawTerminalEvent) {
    return {
      ok: false,
      code: 'STREAM_TRUNCATED',
      message: 'Stream ended without a done or error event',
    };
  }
  return { ok: true };
}

interface FrameSeparator {
  start: number;
  end: number;
}

function findFrameSeparator(buffer: string): FrameSeparator | -1 {
  const lf = buffer.indexOf('\n\n');
  const crlf = buffer.indexOf('\r\n\r\n');
  if (lf === -1 && crlf === -1) return -1;
  if (lf === -1) return { start: crlf, end: crlf + 4 };
  if (crlf === -1) return { start: lf, end: lf + 2 };
  if (lf < crlf) return { start: lf, end: lf + 2 };
  return { start: crlf, end: crlf + 4 };
}

/** Parse one SSE frame (text between separators). Returns the first `data:`
 *  JSON payload that validates as a CoachStreamEvent; nulls on malformed
 *  frames so the caller can keep reading. */
function parseFrame(frame: string): CoachStreamEvent | null {
  const lines = frame.split(/\r?\n/);
  for (const line of lines) {
    if (!line.startsWith('data:')) continue;
    const payload = line.slice(5).trimStart();
    if (!payload) continue;
    try {
      const parsed = JSON.parse(payload) as unknown;
      if (isCoachStreamEvent(parsed)) return parsed;
    } catch {
      // Malformed JSON — ignore and keep scanning.
    }
  }
  return null;
}

/** SubtleCrypto SHA-256 of a UTF-8 string → lower-hex. See the x-amz-content-
 *  sha256 comment above for why this is required on every streaming POST. */
async function sha256Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  const hex = Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return hex;
}

function isCoachStreamEvent(x: unknown): x is CoachStreamEvent {
  if (typeof x !== 'object' || x === null || !('kind' in x)) return false;
  const kind = (x as { kind: unknown }).kind;
  return kind === 'stage' || kind === 'token' || kind === 'done' || kind === 'error';
}
